import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, UserPlus, Gift } from 'lucide-react';

const LOGO_URL = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_crypto-remit-1/artifacts/hfmyqknz_ChatGPT%20Image%20Sep%2016%2C%202026%2C%2002_56_22%20PM.png';
const COUNTRIES = ['Canada', 'Cameroun', 'France', 'Belgique', 'Suisse', 'Sénégal', "Côte d'Ivoire", 'Mali', 'Burkina Faso', 'Bénin', 'Togo', 'Niger', 'Gabon', 'Congo', 'RDC', 'Guinée', 'Tchad', 'République centrafricaine', 'Rwanda', 'Haïti', 'États-Unis', 'Royaume-Uni', 'Allemagne'];

export default function MboaSendRegister() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ref = params.get('ref') || localStorage.getItem('mboa_ref') || '';
  const [form, setForm] = useState({ first_name: '', last_name: '', email: params.get('email') || '', phone: '', password: '', country: 'Canada', referral_code: ref });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [parrainName, setParrainName] = useState('');
  const [inviteValid, setInviteValid] = useState(!!ref && ref.trim().length >= 5);

  useEffect(() => {
    const code = (form.referral_code || '').trim();
    if (!code || code.length < 5) { setParrainName(''); setInviteValid(false); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get(`/referral/resolve/${encodeURIComponent(code)}`)
        .then(r => { if (!cancelled) { setInviteValid(!!r.data?.valid); setParrainName(r.data?.valid ? (r.data.parrain_first_name || '') : ''); } })
        .catch(() => { if (!cancelled) setParrainName(''); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.referral_code]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.password) {
      toast.error('Remplissez tous les champs obligatoires'); return;
    }
    if (form.password.length < 6) { toast.error('Mot de passe : 6 caractères minimum'); return; }
    setLoading(true);
    try {
      await register({ ...form, app_mode: 'mboasend', ref_code: form.referral_code });
      toast.success('Compte créé ! Vérifiez votre email.');
      navigate('/mboasend/verify-email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'inscription');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F5] flex items-center justify-center p-4" data-testid="mboasend-register">
      <div className="w-full max-w-sm space-y-5">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <img src={LOGO_URL} alt="MBOA" className="w-10 h-10 rounded-lg" />
            <div>
              <span className="text-[#006400] text-xl font-bold" style={{ fontFamily: 'Outfit' }}>MBOA</span>
              <span className="text-[#0D2612] text-xl font-light">Send</span>
            </div>
          </div>
          <p className="text-sm text-slate-600">Créez votre compte pour envoyer de l'argent</p>
        </div>

        {inviteValid && (
          <div className="flex items-center gap-3 bg-[#006400]/8 border border-[#006400]/25 rounded-xl p-3" data-testid="invite-banner">
            <div className="w-9 h-9 rounded-full bg-[#006400]/15 flex items-center justify-center flex-shrink-0">
              <Gift className="w-4 h-4 text-[#006400]" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#006400]">
                {parrainName ? `Invité par ${parrainName} — 10 $ offerts !` : '10 $ offerts !'}
              </p>
              <p className="text-[11px] text-slate-600">Recevez 10 $ de crédit dès votre premier envoi de 100 $ ou plus.</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 shadow-sm rounded-xl p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-600 text-xs">Prénom *</Label>
              <Input value={form.first_name} onChange={e => update('first_name', e.target.value)}
                placeholder="Jean" className="bg-white border-slate-300 text-[#0D2612] h-10 mt-1 text-sm" />
            </div>
            <div>
              <Label className="text-slate-600 text-xs">Nom *</Label>
              <Input value={form.last_name} onChange={e => update('last_name', e.target.value)}
                placeholder="Dupont" className="bg-white border-slate-300 text-[#0D2612] h-10 mt-1 text-sm" />
            </div>
          </div>
          <p className="text-[9px] text-red-400">Inscrivez vos noms et prénoms tels que sur votre pièce d'identité</p>
          <div>
            <Label className="text-slate-600 text-xs">Email *</Label>
            <Input type="email" value={form.email} onChange={e => update('email', e.target.value)}
              placeholder="jean@email.com" className="bg-white border-slate-300 text-[#0D2612] h-10 mt-1 text-sm" />
          </div>
          <div>
            <Label className="text-slate-600 text-xs">Téléphone</Label>
            <Input value={form.phone} onChange={e => update('phone', e.target.value)}
              placeholder="+1 514 000 0000" className="bg-white border-slate-300 text-[#0D2612] h-10 mt-1 text-sm" />
          </div>
          <div>
            <Label className="text-slate-600 text-xs">Pays</Label>
            <select value={form.country} onChange={e => update('country', e.target.value)}
              className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-[#0D2612] mt-1">
              {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-slate-600 text-xs">Mot de passe *</Label>
            <div className="relative mt-1">
              <Input type={showPass ? 'text' : 'password'} value={form.password} onChange={e => update('password', e.target.value)}
                placeholder="6 caractères minimum" className="bg-white border-slate-300 text-[#0D2612] h-10 pr-10 text-sm" />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-2.5 text-slate-600">
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-slate-600 text-xs">Code de parrainage (optionnel)</Label>
            <Input value={form.referral_code} onChange={e => update('referral_code', e.target.value.toUpperCase())}
              placeholder="Ex : JEAN123 — vu sur une affiche ou reçu d'un ami"
              data-testid="register-referral-code"
              className="bg-white border-slate-300 text-[#006400] h-10 mt-1 text-sm placeholder:text-slate-400 uppercase" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#006400] text-white hover:bg-[#004D00] font-semibold h-11">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Créer mon compte
          </Button>
        </form>

        <p className="text-center text-sm text-slate-600">
          Déjà inscrit ? <Link to="/mboasend/login" className="text-[#006400] font-medium hover:underline">Se connecter</Link>
        </p>

        <div className="flex flex-wrap justify-center gap-2 text-[9px] text-slate-400">
          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">Confidentialité</a>
          <span>|</span>
          <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">CGU</a>
          <span>|</span>
          <a href="/aml-kyc-policy" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">AML/KYC</a>
        </div>
      </div>
    </div>
  );
}
